import * as React from 'react'
import {
  Button,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  Box,
  Loading,
} from '@lightspeed/design-system-react'
import { useQuery } from '@apollo/client'
import { ApiCustomer } from '@vend/types'
import { useT } from '@transifex/react'
import { STEPS } from '../types'
import { CustomerInfo } from '../../../common/types'
import { StepBreadcrumbs } from '../StepBreadcrumbs'
import {
  ServiceItem,
  ServiceItemData,
} from '../../../../../../models/sale.constants'
import {
  ListServiceItemsVariables,
  ServiceItemsData,
  listServiceItems,
} from '../../../../../../queries/services'
import { SelectedItemView } from './SelectedItemView'
import { ItemSelectForm, DropdownItem } from './ItemSelectForm'
import { ItemInputForm } from './ItemInputForm'
import { ButtonValue, ItemSegmentedControl } from './ItemSegmentedControl'

interface Props {
  newCustomer: CustomerInfo | null
  selectedCustomer: ApiCustomer | null
  serviceItem?: ServiceItemData
  segButton: ButtonValue
  onClose: (shouldCreate?: boolean) => void
  onStepChange: (step: STEPS) => void
  onServiceItemChange: (serviceItem: ServiceItemData) => void
  onSegButtonSelect: (segButton: ButtonValue) => void
}

export const StepServiceItem: React.FC<Props> = ({
  newCustomer,
  selectedCustomer,
  serviceItem,
  segButton,
  onClose,
  onStepChange,
  onServiceItemChange,
  onSegButtonSelect,
}) => {
  const [selectedButton, setSelectedButton] = React.useState(segButton)

  const defaultItemDetails = React.useMemo(
    () => ({
      itemId: '',
      itemName: '',
      serialNumber: '',
      description: '',
      initialCondition: '',
    }),
    []
  )

  const [itemDetailsInputs, setItemDetailsInputs] = React.useState(
    serviceItem?.itemDetails || defaultItemDetails
  )

  const [hasSelectedExistingItem, setHasSelectedExistingItem] = React.useState(
    // a newly created item would not have an itemId yet
    itemDetailsInputs.itemId !== ''
  )

  const { data, loading, error } = useQuery<
    ServiceItemsData,
    ListServiceItemsVariables
  >(listServiceItems, {
    variables: {
      customerId: selectedCustomer?.id || '',
    },
    fetchPolicy: 'no-cache',
    skip: !selectedCustomer?.id || segButton === ButtonValue.SKIP_ADD_ITEM,
  })
  const serviceItems = data?.listServiceItems?.serviceItems

  const [existingItems, setExistingItems] = React.useState<ServiceItem[] | []>(
    []
  )
  const [shouldShowDropdown, setShouldShowDropdown] = React.useState(false)

  React.useEffect(() => {
    if (serviceItems && serviceItems.length > 0) {
      setExistingItems(serviceItems)
      setShouldShowDropdown(!error)
      // check if the serviceItem exists in the list of items
      if (hasSelectedExistingItem) {
        // the scenario where the merchant changes the customer after selecting an item.
        // check if the selected item exists for the currently selected customer.
        const isItemOfCustomer = serviceItems.some(
          existingItem => existingItem.itemId === itemDetailsInputs.itemId
        )
        if (!isItemOfCustomer) {
          setItemDetailsInputs(defaultItemDetails)
          setShouldShowDropdown(true)
          setHasSelectedExistingItem(false)
        }
      }
    }
  }, [
    defaultItemDetails,
    error,
    hasSelectedExistingItem,
    itemDetailsInputs.itemId,
    serviceItems,
  ])

  const [
    shouldDisplayAuxiliaryBackButton,
    setShouldDisplayAuxiliaryBackButton,
  ] = React.useState(false)

  const hasFilledItemDetailsInputs = () =>
    Object.values(itemDetailsInputs).some(value => value !== '')

  const handleServiceItemBack = () => {
    onServiceItemChange({
      ...serviceItem,
      itemDetails: itemDetailsInputs,
    })
    onStepChange(STEPS.SELECT_CUSTOMER)
  }

  const handleServiceItemNext = () => {
    onServiceItemChange({
      ...serviceItem,
      itemDetails: itemDetailsInputs,
    })
    onStepChange(STEPS.WORK_DESCRIPTION)
  }

  const isNextDisabled = () => {
    return (
      selectedButton === ButtonValue.ADD_ITEM &&
      !itemDetailsInputs.itemName?.trim()
    )
  }

  const handleSegChange = (event: { target: { name: any; value: any } }) => {
    const value = event.target.value
    onSegButtonSelect(value)
    if (value === ButtonValue.SKIP_ADD_ITEM) {
      setItemDetailsInputs({
        itemId: '',
        itemName: '',
        serialNumber: '',
        description: '',
        initialCondition: '',
      })
      setHasSelectedExistingItem(false)
    }
    setSelectedButton(value)
  }

  const handleInputChange = (event: { target: { name: any; value: any } }) => {
    const name = event.target.name
    const value = event.target.value
    setItemDetailsInputs(values => ({ ...values, [name]: value }))
  }

  const handleSelectChange = (item: DropdownItem) => {
    const serviceItem = existingItems.find(
      existingItem => existingItem.itemId === item.id
    )
    if (serviceItem) {
      setHasSelectedExistingItem(true)
      setItemDetailsInputs(serviceItem)
      setShouldShowDropdown(false)
    }
  }

  const handleSelectExistingCustomerItem = () => {
    setItemDetailsInputs({
      itemId: '',
      itemName: '',
      serialNumber: '',
      description: '',
      initialCondition: '',
    })
    setHasSelectedExistingItem(false)
    setShouldShowDropdown(true)
  }

  const renderModalContent = () => {
    if (loading) {
      return <Loading />
    }
    if (selectedButton === ButtonValue.ADD_ITEM) {
      if (shouldShowDropdown && !hasFilledItemDetailsInputs()) {
        if (shouldDisplayAuxiliaryBackButton) {
          setShouldDisplayAuxiliaryBackButton(false)
        }
        // Render the dropdown if there are existing items and the user hasn't filled out any of the inputs (eg: merchant enters some details and then navigates back to this step)
        return (
          <ItemSelectForm
            existingItems={existingItems}
            handleSelectChange={handleSelectChange}
            setShouldShowDropdown={setShouldShowDropdown}
          />
        )
      } else if (hasSelectedExistingItem) {
        if (!shouldDisplayAuxiliaryBackButton) {
          setShouldDisplayAuxiliaryBackButton(true)
        }
        // if the user has selected an existing item render the selected item badge and the input form with prefilled values
        return (
          <>
            <SelectedItemView serviceItem={itemDetailsInputs} />
            <ItemInputForm
              itemDetailsInputs={itemDetailsInputs}
              handleInputChange={handleInputChange}
            />
          </>
        )
      } else {
        if (existingItems.length > 0) {
          if (!shouldDisplayAuxiliaryBackButton) {
            setShouldDisplayAuxiliaryBackButton(true)
          }
        }
        // Render the input form if the user has selected to add a new item or there are no existing items
        return (
          <ItemInputForm
            itemDetailsInputs={itemDetailsInputs}
            handleInputChange={handleInputChange}
          />
        )
      }
    }
    // hide the auxiliary back button if merchant choses an existing item and then selects "no item" option
    if (shouldDisplayAuxiliaryBackButton) {
      setShouldDisplayAuxiliaryBackButton(false)
    }
    return null
  }
  const t = useT()

  const serviceItemCopies = {
    header: t(`Add the customer’s item to be worked on`, {
      _context: 'Service item header for service item step in service modal.',
    }),
    selectExistingItem: t(`Select existing customer item`, {
      _context:
        'Text for adding an existing item for service item step in service modal.',
    }),
    back: t(`Back`, {
      _context: 'Back button text for service item step in service modal.',
    }),
    next: t('Next', {
      _context: 'Next button text for service item step in service modal.',
    }),
  }

  return (
    <Modal onClose={() => onClose()}>
      <StepBreadcrumbs step={STEPS.CUSTOMER_ITEM} />
      <ModalHeader>{serviceItemCopies.header}</ModalHeader>
      <ModalContent>
        <ItemSegmentedControl
          selectedButton={selectedButton}
          handleSegChange={handleSegChange}
        />
        {renderModalContent()}
      </ModalContent>
      <ModalActions
        className={
          shouldDisplayAuxiliaryBackButton
            ? 'vd-flex vd-flex--justify-between vd-flex--align-center'
            : ''
        }
      >
        {shouldDisplayAuxiliaryBackButton && (
          <Button
            link
            data-track="select-existing-customer"
            onClick={handleSelectExistingCustomerItem}
          >
            {serviceItemCopies.selectExistingItem}
          </Button>
        )}
        <Box className="vd-btn-group">
          <Button
            variant="supplementary"
            onClick={handleServiceItemBack}
            data-cy="button-back"
            data-track="service-item-back"
          >
            {serviceItemCopies.back}
          </Button>
          <Button
            variant="go"
            onClick={handleServiceItemNext}
            data-cy="button-create-service"
            disabled={isNextDisabled()}
            data-track="service-item-next"
          >
            Next
          </Button>
        </Box>
      </ModalActions>
    </Modal>
  )
}

StepServiceItem.displayName = 'StepServiceItem'
